<script setup lang="ts">
import { ColumnsInterface, FilteringDisplayOptions } from '../../types/components/data-rendering'
import { useModelWrapper } from '../wrappers/helpers/useModelWrapper'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { LanguageChoice } from 'app/i18n'

const props = withDefaults(
  defineProps<{
    index: number
    columns: ColumnsInterface
    filter?: string | null
    checkbox?: FilteringDisplayOptions['checks']
    route?: FilteringDisplayOptions['route']
    cellOptions?: FilteringDisplayOptions['cellOptions']
    showCheckbox?: boolean
    checkedValue?: boolean
    row: any
  }>(),
  {
    filter: null,
    checkbox: undefined,
    route: undefined,
    cellOptions: undefined,
    showCheckbox: false,
    checkedValue: false,
  }
)

const emit = defineEmits<{
  (e: 'update:checked'): void
}>()

const { locale } = useI18n({ useScope: 'global' })
const localeString = computed(() => locale.value as LanguageChoice)

const checked = useModelWrapper(props, emit, 'checkedValue')
const returnCellType = (key: string) => {
  return props.columns[key].type
}

const renderedText = (text: string) => {
  if (props.filter && props.filter.length > 0) {
    const re = new RegExp(props.filter, 'ig')
    if (text != undefined) {
      const results = text.replaceAll(re, '<mark>$&</mark>')
      return results
    }
  }
  return text
}

const renderTime = (duration: string): string => {
  //duration in minutes
  const hours = Math.floor(+duration / 60)
  const minutes = +duration % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}
//date should render a specific way
const renderDate = (value: Date | string | null | undefined) => {
  if (typeof value === 'undefined' || value === null || value === '') return ''
  else if (Object.prototype.toString.call(value) === '[object Date]') {
    if (!value) {
      return null
    }
    // reformat dates to MM/DD/YYYY
    return `
        ${(value as Date).getMonth() + 1}/${(value as Date).getDate()}/${(
      value as Date
    ).getFullYear()}
        `
  } else {
    return `
        ${new Date(value).getMonth() + 1}/${new Date(value).getDate()}/${new Date(
      value
    ).getFullYear()}
        `
  }
}
//values should pass as true or not
const renderBoolean = (value: string | boolean): boolean => {
  if (typeof value === 'boolean') {
    return value
  } else {
    if (value.toUpperCase() == 'T') return true
    if (value.toUpperCase() == 'TRUE') return true
    return value.toUpperCase() === 'YES' ? true : false
  }
}

const rowKeyToString = (data: any) => {
  return typeof data !== 'undefined' && data !== null ? data.toString() : ''
}

const rowClass = computed(() => {
  const out = []
  if (checked.value) {
    out.push('selected')
  }
  if (props.route?.link) {
    out.push('cursor-pointer')
  }
  return out.join(' ')
})
</script>
<template>
  <tr data-test="table-row" :class="rowClass">
    <td class="text-right bg-grey-2 text-grey-7 sticky--left">
      {{ index + 1 }}
    </td>
    <td v-if="checkbox && checkbox.checklist">
      <q-checkbox
        dense
        size="xs"
        v-model="checked"
        v-show="showCheckbox"
        v-bind="checkbox.checkProps"
      />
    </td>
    <template
      v-for="(column, key) in columns"
      :key="`${column.label[localeString]}_${row[key]}_${row}#:${index}`"
    >
      <td
        v-if="!column.hide"
        @click.stop="
          cellOptions && cellOptions.atCellClick
            ? cellOptions.atCellClick(key, row)
            : route?.link
            ? $router.push({ path: `${route.link(row)}` })
            : undefined
        "
        :class="cellOptions && cellOptions.cellClass ? cellOptions.cellClass(key) : undefined"
      >
        <slot
          :key="`${column.label[localeString]}_${row[key]}_${row}#:${index}`"
          :name="key"
          :row-data="row"
          :filter-data="filter"
        >
          <!-- String or Number -->
          <template v-if="returnCellType(key) === 'string' || returnCellType(key) === 'number'">
            <template v-if="filter && filter.length > 0">
              <div v-html="renderedText(rowKeyToString(row[key]).toString())"></div>
            </template>
            <template v-else>
              {{ rowKeyToString(row[key]).toString() }}
            </template>
          </template>
          <!-- Time -->
          <template v-if="returnCellType(key) === 'time'">
            <template v-if="filter && filter.length > 0">
              <div
                :class="{ 'break-word': wordWrap }"
                v-html="renderedText(rowKeyToString(row[key]).toString())"
              ></div>
            </template>
            <template v-else>
              {{ renderTime(rowKeyToString(row[key])) }}
            </template>
          </template>
          <!-- Booleans -->
          <template v-if="returnCellType(key) === 'boolean'">
            <i v-if="renderBoolean(rowKeyToString(row[key]))" class="las la-check"></i>
          </template>
          <!-- Date -->
          <template v-if="returnCellType(key) === 'date'">
            {{ renderDate(rowKeyToString(row[key])) }}
          </template>
        </slot>
      </td>
    </template>
    <td v-if="$slots.itemRight">
      <slot name="itemRight" :row-data="row" />
    </td>
  </tr>
</template>
